import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function TicketManagement(){
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);

  useEffect(()=>{ fetchEvents(); }, []);

  async function fetchEvents(){
    setLoading(true); setError(null);
    try{
      const r = await api.get('/events/manager');
      setEvents(Array.isArray(r.data) ? r.data : []);
    }catch(e){ setError(e?.userMessage || e?.message || 'Failed to load events'); setEvents([]); }
    finally{ setLoading(false); }
  }

  function updateLocal(id, patch){
    setEvents(prev => prev.map(ev => ev.id === id ? { ...ev, ...patch } : ev));
  }

  async function savePrice(id, value){
    setSaving(s => ({ ...s, [id]: true }));
    try{
      const payload = { ticket_price: parseFloat(value) };
      await api.put(`/events/${id}/price`, payload);
      updateLocal(id, { ticket_price: payload.ticket_price });
    }catch(e){ alert('Save failed: ' + (e?.message || 'unknown')) }
    finally{ setSaving(s => ({ ...s, [id]: false })); }
  }

  async function saveCapacity(id, value){
    setSaving(s => ({ ...s, ['c'+id]: true }));
    try{
      const payload = { capacity: parseInt(value,10) };
      await api.put(`/events/${id}/capacity`, payload);
      updateLocal(id, { capacity: payload.capacity });
    }catch(e){ alert('Save failed: ' + (e?.message || 'unknown')) }
    finally{ setSaving(s => ({ ...s, ['c'+id]: false })); }
  }

  if (!user) return <div>Please login</div>;

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Ticket Management</h1>
        <p className="text-sm text-gray-600">Manage ticket prices, capacity and view availability for your shows.</p>
      </header>

      {error && <div className="text-red-600 mb-4">{error}</div>}

      {loading ? (
        <div className="p-4 bg-white rounded shadow">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="p-4 bg-white rounded shadow">No events found.</div>
      ) : (
        <div className="grid gap-4">
          {events.map(ev => {
            const seats_sold = ev.seats_sold ? Number(ev.seats_sold) : 0;
            const capacity = ev.capacity ? Number(ev.capacity) : 100;
            const remaining = Math.max(0, capacity - seats_sold);
            return (
              <div key={ev.id} className="p-4 bg-white rounded shadow flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-semibold">{ev.name}</div>
                      <div className="text-sm text-gray-600">{ev.date} • {ev.time}</div>
                    </div>
                    <div className="text-sm text-gray-500">ID #{ev.id}</div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Seats Sold</div>
                      <div className="font-medium">{seats_sold}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500">Capacity</div>
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} className="border p-2 rounded w-full max-w-[120px]" defaultValue={capacity} onChange={(e)=> updateLocal(ev.id, { capacity: e.target.value })} />
                        <button className="btn btn-sm btn-primary" onClick={()=> saveCapacity(ev.id, ev.capacity)} disabled={!!saving['c'+ev.id]}>{saving['c'+ev.id] ? 'Saving...' : 'Save'}</button>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500">Ticket Price (₹)</div>
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} step="0.01" className="border p-2 rounded w-full max-w-[140px]" defaultValue={ev.ticket_price ?? 0} onChange={(e)=> updateLocal(ev.id, { ticket_price: e.target.value })} />
                        <button className="btn btn-sm btn-primary" onClick={()=> savePrice(ev.id, ev.ticket_price)} disabled={!!saving[ev.id]}>{saving[ev.id] ? 'Saving...' : 'Save'}</button>
                      </div>
                    </div>

                  </div>

                </div>

                <div className="w-full md:w-48">
                  <div className="text-xs text-gray-500">Remaining</div>
                  <div className="text-xl font-bold">{remaining}</div>
                  <div className="mt-3">
                    <div className="text-xs text-gray-500">Occupancy</div>
                    <div className="w-full bg-gray-200 rounded h-3 overflow-hidden mt-1">
                      <div className="bg-indigo-500 h-3" style={{ width: `${Math.round((seats_sold / Math.max(1, capacity)) * 100)}%` }} />
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <input type="number" min={-10000} max={10000} className="border p-2 rounded w-32" placeholder="Delta" id={`delta-${ev.id}`} />
                    <button className="btn btn-sm btn-outline" onClick={async ()=>{
                      const el = document.getElementById(`delta-${ev.id}`);
                      const delta = el ? parseInt(el.value,10) : 0;
                      if (Number.isNaN(delta)) return alert('Enter numeric delta');
                      if (!confirm(`Apply delta ${delta} to capacity of ${ev.name}?`)) return;
                      try{
                        const r = await api.post(`/events/${ev.id}/adjust-seats`, { delta });
                        alert('Capacity updated');
                        // update local state
                        updateLocal(ev.id, { capacity: r.data.event.capacity });
                      }catch(err){ alert('Adjust failed: ' + (err?.response?.data?.message || err?.message || 'error')); }
                    }}>Apply</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
